import React, { useEffect, useCallback, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { 
  ChevronRight, 
  MoreVertical, 
  FilePlus,
  FolderPlus,
  FileIcon,
  FolderIcon,
  BookOpen,
  Target,
  Instagram,
  Calendar,
  Users,
  Trello,
  X
} from 'lucide-react';
import { KnowledgeItem } from '../services/api';
import { ParticipantsIndicator } from './ParticipantsIndicator';



interface MobileKnowledgeOverlayProps {
  isOpen: boolean;
  onClose: () => void;
  items: KnowledgeItem[];
  selectedItem: KnowledgeItem | null;
  expandedFolders: string[];
  isEditing: string | null;
  editName: string;
  onToggleFolder: (folderId: string) => void;
  onSelectItem: (item: KnowledgeItem) => void;
  onEditNameChange: (value: string) => void;
  onEditSave: (item: KnowledgeItem) => void;
  onAddFile: () => void;
  onAddFolder: () => void;
  onContextMenu: (e: React.MouseEvent, item: KnowledgeItem) => void;
  onCloseContextMenu: () => void;
}

export const MobileKnowledgeOverlay: React.FC<MobileKnowledgeOverlayProps> = ({
  isOpen,
  onClose,
  items,
  selectedItem,
  expandedFolders,
  isEditing,
  editName,
  onToggleFolder,
  onSelectItem,
  onEditNameChange,
  onEditSave,
  onAddFile,
  onAddFolder,
  onContextMenu: parentOnContextMenu,
  onCloseContextMenu
}) => {
  const navigate = useNavigate();
  

  

  
  const overlayContentRef = useRef<HTMLDivElement>(null);
  const inputRef = useRef<HTMLInputElement>(null);

  useEffect(() => {
    const contentElement = overlayContentRef.current;
    if (!contentElement) return;

    const handleScroll = () => {
      onCloseContextMenu();
    };

    contentElement.addEventListener('scroll', handleScroll);

    return () => {
      contentElement.removeEventListener('scroll', handleScroll);
    };
  }, [isOpen, onCloseContextMenu]);



  // Эффект для фокуса на инпуте при редактировании
  useEffect(() => {
    if (isEditing && inputRef.current) {
      setTimeout(() => {
        if (inputRef.current) {
          inputRef.current.focus();
          inputRef.current.select();
        }
      }, 50);
    }
  }, [isEditing]);

  // Закрытие при нажатии Escape
  useEffect(() => {
    const handleEscape = (e: KeyboardEvent) => {
      if (e.key === 'Escape') {
        onClose();
      }
    };

    if (isOpen) {
      document.addEventListener('keydown', handleEscape);
    }

    return () => {
      document.removeEventListener('keydown', handleEscape);
    };
  }, [isOpen, onClose]);

  // Функция для получения иконки файла
  const getFileIcon = (fileType?: string) => {
    switch (fileType) {
      case 'article':
      case 'summary':
        return <FileIcon className="w-5 h-5 text-blue-500" strokeWidth={1.5} />;
      case 'notes':
        return <BookOpen className="w-5 h-5 text-purple-500" strokeWidth={1.5} />;
      case 'roadmap':
        return <Target className="w-5 h-5 text-orange-500" strokeWidth={1.5} />;
      case 'reels':
        return <Instagram className="w-5 h-5 text-rose-500" strokeWidth={1.5} />;
      case 'content-plan':
        return <Calendar className="w-5 h-5 text-teal-500" strokeWidth={1.5} />;
      case 'audience':
        return <Users className="w-5 h-5 text-indigo-500" strokeWidth={1.5} />;
      case 'kanban':
        return <Trello className="w-5 h-5 text-red-500" strokeWidth={1.5} />;
      default:
        return <FileIcon className="w-5 h-5 text-gray-500" strokeWidth={1.5} />;
    }
  };



  // Обработчик клика по элементу
  const handleItemClick = useCallback((item: KnowledgeItem) => {
    onSelectItem(item);
    
    // Переходим к элементу если это файл
    if (item.itemType !== 'folder') {
      onClose(); // Закрываем оверлей
      navigate(`/knowledge/${item.id}`);
    }
  }, [onSelectItem, navigate, onClose]);





  // Функция для проверки выбранного элемента
  const isItemSelected = useCallback((itemId: string): boolean => {
    return itemId === selectedItem?.id;
  }, [selectedItem]);



  // Рендеринг элемента списка
  const renderItem = useCallback((item: KnowledgeItem, level: number = 0) => {
    if (!item || !item.id) {
      return null;
    }
    
    const isExpandedFolder = expandedFolders.includes(item.id);
    const selected = isItemSelected(item.id);
    const isItemEditing = isEditing === item.id;
    const isTemporary = item.id.startsWith('temp-');
    
    let displayName = item.name || "";
    if (isTemporary && !item.name) {
      if (item.id.startsWith('temp-folder-')) {
        displayName = "Новая папка";
      } else {
        displayName = "Новый файл";
      }
    } else {
      displayName = item.name || "Без имени";
    }
    
    const hasChildren = item.children && item.children.length > 0;

    return (
      <React.Fragment key={item.id}>
        <div
          className={`group flex items-center py-2 px-4 active:bg-gray-100 dark:active:bg-gray-700 border-b border-gray-100 dark:border-gray-700 cursor-pointer ${
            selected ? 'bg-blue-50 dark:bg-blue-900/20 border-blue-200 dark:border-blue-800' : ''
          }`}
          style={{ paddingLeft: `${level * 1 + 1}rem` }}
          onClick={(e) => {
            e.preventDefault();
            e.stopPropagation();
            
            if (isTemporary) {
              onSelectItem(item);
              if (!isEditing) {
                parentOnContextMenu(e, item);
              }
            } else if (item.itemType === 'folder') {
              onToggleFolder(item.id);
            } else {
              handleItemClick(item);
            }
          }}
          onContextMenu={(e) => parentOnContextMenu(e, item)}
        >
          {/* Иконка раскрытия для папок */}
          {(item.itemType === 'folder' || hasChildren) && (
            <div 
              className="flex items-center justify-center w-6 h-6 mr-3"
              onClick={(e) => {
                e.stopPropagation();
                onToggleFolder(item.id);
              }}
            >
              <ChevronRight
                className={`w-5 h-5 transition-transform text-gray-400 dark:text-gray-300 ${
                  isExpandedFolder ? 'transform rotate-90' : ''
                }`}
                strokeWidth={1.5}
              />
            </div>
          )}
          
          {/* Иконка типа файла/папки */}
          <div className="flex items-center mr-3">
            {item.itemType === 'folder' ? (
              <FolderIcon className="w-5 h-5 text-blue-500" strokeWidth={1.5} />
            ) : (
              <div className="flex items-center">
                {getFileIcon(item.fileType)}
                <div className="ml-2">
                  <ParticipantsIndicator 
                    knowledgeItemId={item.id} 
                    fileType={item.fileType}
                    isShared={item.isShared}
                    knowledgeItem={item}
                  />
                </div>
              </div>
            )}
          </div>
          
          {/* Название элемента или поле редактирования */}
          {isItemEditing ? (
            <input
              ref={inputRef}
              type="text"
              value={editName}
              onChange={(e) => onEditNameChange(e.target.value)}
              onBlur={() => onEditSave(item)}
              onKeyDown={(e) => {
                if (e.key === 'Enter') {
                  onEditSave(item);
                } else if (e.key === 'Escape') {
                  if (item.id.startsWith('temp-')) {
                    onEditNameChange('');
                    onEditSave(item);
                  } else {
                    onEditNameChange(item.name);
                    onEditSave(item);
                  }
                }
              }}
              className="flex-1 bg-white dark:bg-gray-800 px-3 py-2 rounded-lg border border-gray-300 dark:border-gray-600 text-gray-900 dark:text-white text-base"
              placeholder="Введите имя"
              autoFocus
              onFocus={(e) => e.target.select()}
            />
          ) : (
            <span className="flex-1 text-gray-900 dark:text-white text-base font-medium line-clamp-2 leading-tight">
              {displayName}
            </span>
          )}
          
          {/* Кнопка меню (три точки) */}
          <button
            onClick={(e) => {
              e.stopPropagation();
              parentOnContextMenu(e, item);
            }}
            className="p-3 rounded-full active:bg-gray-200 dark:active:bg-gray-600 transition-all flex-shrink-0 ml-3"
          >
            <MoreVertical className="w-5 h-5 text-gray-500 dark:text-gray-400" strokeWidth={1.5} />
          </button>
        </div>
        
        {/* Дочерние элементы */}
        {isExpandedFolder && hasChildren && item.children && (
          <div>
            {item.children.filter(child => child && child.id).map(child => renderItem(child, level + 1))}
          </div>
        )}
      </React.Fragment>
    );
  }, [
    expandedFolders, 
    isEditing, 
    editName, 
    onToggleFolder, 
    onSelectItem, 
    parentOnContextMenu, 
    isItemSelected, 
    handleItemClick,
    onEditNameChange,
    onEditSave
  ]);

  if (!isOpen) {
    return null;
  }

  return (
    <>
      {/* Backdrop */}
      <div 
        className="fixed inset-0 bg-black/50 z-50 md:hidden"
        onClick={onClose}
      />
      
      {/* Overlay Content */}
      <div className="fixed inset-0 z-50 md:hidden">
        <div className="h-full bg-white dark:bg-gray-900 flex flex-col">
          {/* Header */}
          <div className="flex items-center justify-between p-4 border-b border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-800">
            <div className="flex items-center">
              <button
                onClick={onClose}
                className="p-2 mr-3 rounded-full hover:bg-gray-100 dark:hover:bg-gray-700 transition-colors"
              >
                <X className="w-6 h-6 text-gray-600 dark:text-gray-300" strokeWidth={2} />
              </button>
              <h1 className="text-xl font-semibold text-gray-900 dark:text-white">База знаний</h1>
            </div>
            
            {/* Кнопки добавления */}
            <div className="flex items-center space-x-2">
              {false && (
                <button 
                  onClick={onAddFile} 
                  title="Добавить файл"
                  className="p-2 hover:bg-gray-100 dark:hover:bg-gray-700 rounded-full transition-colors"
                >
                  <FilePlus className="w-6 h-6 text-blue-500 dark:text-blue-400" strokeWidth={1.5} />
                </button>
              )}
              <button 
                onClick={onAddFolder} 
                title="Добавить папку"
                className="p-2 hover:bg-gray-100 dark:hover:bg-gray-700 rounded-full transition-colors"
              >
                <FolderPlus className="w-6 h-6 text-blue-500 dark:text-blue-400" strokeWidth={1.5} />
              </button>
            </div>
          </div>

          {/* Content */}
          <div ref={overlayContentRef} className="flex-1 overflow-y-auto">
            {Array.isArray(items) && items.filter(item => item && item.id).map(item => renderItem(item))}
            
            {/* Empty state */}
            {(!items || items.length === 0) && (
              <div className="flex flex-col items-center justify-center h-full p-8 text-center">
                <BookOpen className="w-16 h-16 text-gray-300 dark:text-gray-600 mb-4" />
                <h3 className="text-lg font-medium text-gray-500 dark:text-gray-400 mb-2">
                  База знаний пуста
                </h3>
                <p className="text-gray-400 dark:text-gray-500 mb-6">
                  Создайте первый файл или папку для начала работы
                </p>
                <div className="flex space-x-4">
                  {false && (
                    <button 
                      onClick={onAddFile}
                      className="flex items-center px-4 py-2 bg-blue-500 text-white rounded-lg hover:bg-blue-600 transition-colors"
                    >
                      <FilePlus className="w-5 h-5 mr-2" />
                      Создать файл
                    </button>
                  )}
                  <button 
                    onClick={onAddFolder}
                    className="flex items-center px-4 py-2 border border-gray-300 dark:border-gray-600 text-gray-700 dark:text-gray-300 rounded-lg hover:bg-gray-50 dark:hover:bg-gray-800 transition-colors"
                  >
                    <FolderPlus className="w-5 h-5 mr-2" />
                    Создать папку
                  </button>
                </div>
              </div>
            )}
          </div>
        </div>
      </div>


    </>
  );
};